// Marketing plan comparison matrix.
//
// Single source of truth for the comparison table rendered in three places:
//   - /tarifs Section D (collapsible, expanded by default on desktop)
//   - /tarifs/comparatif (full page, printable, emailable)
//   - marketing-send-comparison edge function (HTML render for email)
//
// Values are intentionally hard-coded here rather than read from
// packages.package_features, because:
//   • Marketing copy and database operational config drift independently
//     (e.g. the DB stores "stock_only" while marketing says "banque seule").
//   • The page needs to render without a network call (Lighthouse / SEO).
//   • The matrix carries categories and ordering that the DB does not.
// The numerical values (credits, sites, sources, seats) intentionally
// mirror the v2 seed in 20260514230000_credits_features_models.sql.
// Keep both in sync when prices/features change.
//
// Locale: only "fr" and "en" are emitted; bilingual leaves are stored as
// { fr, en } objects so the same module powers FR and EN comparisons.

export type PlanSlug = "starter" | "growth" | "network" | "agency" | "enterprise";

export const PLAN_SLUGS: PlanSlug[] = ["starter", "growth", "network", "agency", "enterprise"];

import { intlLocale, type Locale } from "@/lib/locale";

/** Localised leaf, used for human-readable strings only. */
export interface BilingualText {
  fr: string;
  en: string;
  /** Machine-translated from `en` by scripts/i18n/translate.mjs. */
  es?: string;
  ar?: string;
}

/** What a single row resolves to for one plan. */
export type CellValue =
  | { kind: "boolean"; value: boolean }
  | { kind: "number"; value: number; unit?: BilingualText }
  | { kind: "text"; value: BilingualText }
  | { kind: "unlimited" }
  | { kind: "custom" }
  | { kind: "dash" };

export interface PlanMatrixRow {
  id: string;
  label: BilingualText;
  hint?: BilingualText;
  cells: Record<PlanSlug, CellValue>;
}

export interface PlanMatrixCategory {
  id: string;
  label: BilingualText;
  rows: PlanMatrixRow[];
}

// ---- helpers used only at module-build time ---------------------------------

const T = (fr: string, en: string, es?: string, ar?: string): BilingualText => ({ fr, en, es, ar });
const num = (n: number, unit?: BilingualText): CellValue => ({ kind: "number", value: n, unit });
const yes = (): CellValue => ({ kind: "boolean", value: true });
const no = (): CellValue => ({ kind: "boolean", value: false });
const txt = (fr: string, en: string): CellValue => ({ kind: "text", value: T(fr, en) });
const unlim: CellValue = { kind: "unlimited" };
const custom: CellValue = { kind: "custom" };
const dash: CellValue = { kind: "dash" };

// Standard units reused across rows.
const U_SITES = T("sites", "sites", "sitios", "المواقع");
const U_ARTICLES = T("articles / mois", "articles / month", "artículos / mes", "مقالات / شهر");
const U_CREDITS = T("crédits / mois", "credits / month", "créditos / mes", "رصيد / شهر");
const U_SOURCES = T("sources", "sources", "fuentes", "المصادر");
const U_LANGS = T("langues", "languages", "idiomas", "اللغات");
const U_SEATS = T("sièges", "seats", "asientos", "المقاعد");

// ---- data -------------------------------------------------------------------

export const PLAN_DISPLAY_NAMES: Record<PlanSlug, BilingualText> = {
  starter:    T("Starter", "Starter", "Starter", "Starter"),
  growth:     T("Growth", "Growth", "Growth", "Growth"),
  network:    T("Network", "Network", "Network", "Network"),
  agency:     T("Agency", "Agency", "Agencia", "وكالة"),
  enterprise: T("Enterprise", "Enterprise", "Empresa", "المؤسسات"),
};

export const PLAN_AUDIENCE: Record<PlanSlug, BilingualText> = {
  starter:    T("Éditeurs solo", "Solo publishers", "Editores individuales", "الناشرون الأفراد"),
  growth:     T("Marques en croissance", "Growing brands", "Marcas en crecimiento", "العلامات التجارية الناشئة"),
  network:    T("Réseaux de sites", "Site networks", "Redes de sitios", "شبكات المواقع"),
  agency:     T("Agences & médias", "Agencies & media", "Agencias y medios", "الوكالات والإعلام"),
  enterprise: T("Groupes & ETI", "Enterprise groups", "Grupos empresariales", "مجموعات المؤسسات"),
};

/** Monthly EUR price in cents, mirrors packages.price_cents. */
export const PLAN_MONTHLY_CENTS: Record<PlanSlug, number | null> = {
  starter:    7900,
  growth:    19900,
  network:   59900,
  agency:    99900,
  enterprise: null,
};

/** Setup fee in cents — mirrors setup_fees.amount_cents. */
export const PLAN_SETUP_CENTS: Record<PlanSlug, number | null> = {
  starter:     4900,
  growth:     29900,
  network:    79900,
  agency:    149900,
  enterprise: 249900,
};

export const PLAN_MATRIX: PlanMatrixCategory[] = [
  {
    id: "volume",
    label: T("Volume", "Volume", "Volumen", "الحجم"),
    rows: [
      {
        id: "sites",
        label: T("Sites publiés", "Live sites", "Sitios en directo", "المواقع النشطة"),
        cells: {
          starter:    num(1,  U_SITES),
          growth:     num(3,  U_SITES),
          network:    num(10, U_SITES),
          agency:     num(25, U_SITES),
          enterprise: unlim,
        },
      },
      {
        id: "articles_per_month",
        label: T("Articles publiés / mois (plafond)", "Published articles / month (cap)", "Artículos publicados / mes (límite)", "المقالات المنشورة / شهر (الحد الأقصى)"),
        hint: T(
          "Plafond appliqué par la plateforme. Les packs de crédits ne le relèvent pas ; à cadence standard, 1 article ≈ 3 crédits.",
          "Cap enforced by the platform. Credit packs do not lift it; at standard quality, 1 article ≈ 3 credits.",
        ),
        cells: {
          starter:    num(130, U_ARTICLES),
          growth:     num(500, U_ARTICLES),
          network:    num(1800, U_ARTICLES),
          agency:     num(5000, U_ARTICLES),
          enterprise: unlim,
        },
      },
      {
        id: "monthly_credits",
        label: T("Crédits inclus", "Included credits", "Créditos incluidos", "الرصيد المضمن"),
        cells: {
          starter:    num(400,   U_CREDITS),
          growth:     num(1500,  U_CREDITS),
          network:    num(5500,  U_CREDITS),
          agency:     num(12000, U_CREDITS),
          enterprise: num(30000, U_CREDITS),
        },
      },
      {
        id: "max_sources",
        label: T("Sources d'actualités connectées", "Connected news sources", "Fuentes de noticias conectadas", "مصادر الأخبار المتصلة"),
        cells: {
          starter:    num(10,  U_SOURCES),
          growth:     num(30,  U_SOURCES),
          network:    num(100, U_SOURCES),
          agency:     num(200, U_SOURCES),
          enterprise: unlim,
        },
      },
    ],
  },
  {
    id: "ai",
    label: T("Génération IA", "AI generation", "Generación con IA", "إنشاء الذكاء الاصطناعي"),
    rows: [
      {
        id: "ai_models",
        label: T("Modèles IA disponibles", "Available AI models", "Modelos de IA disponibles", "نماذج الذكاء الاصطناعي المتاحة"),
        cells: {
          starter:    txt("Haiku 4.5",                "Haiku 4.5"),
          growth:     txt("Haiku, Sonnet 4.6",         "Haiku, Sonnet 4.6"),
          network:    txt("Haiku, Sonnet, Opus 4.7",   "Haiku, Sonnet, Opus 4.7"),
          agency:     txt("Tous Claude + Gemini",      "All Claude + Gemini"),
          enterprise: txt("Tous + modèles dédiés",     "All + dedicated models"),
        },
      },
      {
        id: "quality_tier",
        label: T("Niveau de qualité", "Quality tier", "Nivel de calidad", "مستوى الجودة"),
        cells: {
          starter:    txt("Standard",  "Standard"),
          growth:     txt("Avancé",    "Advanced"),
          network:    txt("Premium",   "Premium"),
          agency:     txt("Flagship",  "Flagship"),
          enterprise: txt("Sur mesure","Custom"),
        },
      },
      {
        id: "editorial_qa",
        label: T("Contrôle éditorial automatique", "Automated editorial QA", "Control de calidad editorial automatizado", "مراجعة تحريرية آلية"),
        cells: {
          starter:    no(),
          growth:     yes(),
          network:    yes(),
          agency:     yes(),
          enterprise: yes(),
        },
      },
      {
        id: "approval_queue",
        label: T("File d'approbation", "Approval queue", "Cola de aprobación", "قائمة الموافقة"),
        cells: {
          starter:    txt("Optionnelle", "Optional"),
          growth:     txt("Optionnelle", "Optional"),
          network:    txt("Workflow",    "Workflow"),
          agency:     txt("Workflow",    "Workflow"),
          enterprise: txt("Workflow",    "Workflow"),
        },
      },
    ],
  },
  {
    id: "images",
    label: T("Images", "Images", "Imágenes", "الصور"),
    rows: [
      {
        id: "images_basic",
        label: T("Génération basique (Flux Schnell)", "Basic generation (Flux Schnell)", "Generación básica (Flux Schnell)", "إنشاء أساسي (Flux Schnell)"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "images_premium",
        label: T("Génération premium (Flux Ultra)", "Premium generation (Flux Ultra)", "Generación premium (Flux Ultra)", "إنشاء مميز (Flux Ultra)"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "images_stock",
        label: T("Banques stock (Unsplash, Pexels)", "Stock libraries (Unsplash, Pexels)", "Bibliotecas de stock (Unsplash, Pexels)", "مكتبات الصور (Unsplash, Pexels)"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
    ],
  },
  {
    id: "seo",
    label: T("SEO", "SEO", "SEO", "SEO"),
    rows: [
      {
        id: "seo_basic",
        label: T("SEO basique (meta + canonical)", "Basic SEO (meta + canonical)", "SEO básico (meta + canonical)", "SEO أساسي (meta + canonical)"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "seo_advanced",
        label: T("SEO avancé (sitemap, hreflang)", "Advanced SEO (sitemap, hreflang)", "SEO avanzado (sitemap, hreflang)", "SEO متقدم (sitemap, hreflang)"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "seo_schema",
        label: T("Schema.org NewsArticle", "Schema.org NewsArticle", "Schema.org NewsArticle", "Schema.org NewsArticle"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "seo_geo",
        label: T("SEO géo-localisé", "Geo-targeted SEO", "SEO geolocalizado", "SEO مستهدف جغرافيًا"),
        cells: { starter: no(), growth: no(), network: no(), agency: yes(), enterprise: yes() },
      },
    ],
  },
  {
    id: "publishing",
    label: T("Publication", "Publishing", "Publicación", "النشر"),
    rows: [
      {
        id: "publish_manual",
        label: T("Manuelle", "Manual", "Manual", "يدوي"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "publish_daily",
        label: T("Quotidienne automatique", "Daily automated", "Automatizado diario", "تحديث يومي آلي"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "publish_hourly",
        label: T("Horaire", "Hourly", "Por horas", "تحديث كل ساعة"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "publish_realtime",
        label: T("Temps réel", "Real-time", "En tiempo real", "تحديث في الوقت الفعلي"),
        cells: { starter: no(), growth: no(), network: yes(), agency: yes(), enterprise: yes() },
      },
    ],
  },
  {
    id: "i18n",
    label: T("Internationalisation", "Internationalisation", "Internacionalización", "التدويل"),
    rows: [
      {
        id: "languages_count",
        label: T("Langues de publication", "Publishing languages", "Idiomas de publicación", "لغات النشر"),
        cells: {
          starter:    num(1,  U_LANGS),
          growth:     num(2,  U_LANGS),
          network:    num(5,  U_LANGS),
          agency:     num(10, U_LANGS),
          enterprise: unlim,
        },
      },
      {
        id: "translation_qa",
        label: T("Relecture traduction IA", "Translation QA pass", "Revisión de calidad de traducción", "مراجعة جودة الترجمة"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
    ],
  },
  {
    id: "team",
    label: T("Équipe", "Team", "Equipo", "الفريق"),
    rows: [
      {
        id: "team_seats",
        label: T("Sièges (admins + éditeurs)", "Seats (admins + editors)", "Asientos (administradores + editores)", "المقاعد (المشرفون + المحررون)"),
        cells: {
          starter:    num(2,  U_SEATS),
          growth:     num(5,  U_SEATS),
          network:    num(15, U_SEATS),
          agency:     num(50, U_SEATS),
          enterprise: unlim,
        },
      },
      {
        id: "team_roles",
        label: T("Rôles personnalisables", "Custom roles", "Roles personalizados", "الأدوار المخصصة"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "editorial_approval",
        label: T("Approbation éditoriale", "Editorial approval flow", "Flujo de aprobación editorial", "سير عمل الموافقة التحريرية"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
    ],
  },
  {
    id: "api",
    label: T("API", "API", "API", "API"),
    rows: [
      {
        id: "api_read",
        label: T("Lecture", "Read", "Leer", "اقرأ"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "api_write",
        label: T("Écriture", "Write", "Escritura", "Write"),
        cells: { starter: no(), growth: no(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "api_webhooks",
        label: T("Webhooks", "Webhooks", "Webhooks", "Webhooks"),
        cells: { starter: no(), growth: no(), network: no(), agency: yes(), enterprise: yes() },
      },
    ],
  },
  {
    id: "monetization",
    label: T("Monétisation", "Monetisation", "Monetización", "التداول"),
    rows: [
      {
        id: "affiliate",
        label: T("Affiliation", "Affiliate", "Afiliado", "برنامج تابع"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "ads",
        label: T("Régie publicitaire", "Display ads", "Anuncios display", "إعلانات عرضية"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "programmatic",
        label: T("Publicité programmatique", "Programmatic ads", "Anuncios programáticos", "إعلانات برمجية"),
        cells: { starter: no(), growth: no(), network: yes(), agency: yes(), enterprise: yes() },
      },
    ],
  },
  {
    id: "white_label",
    label: T("Marque blanche", "White-label", "White-label", "العلامة البيضاء"),
    rows: [
      {
        id: "wl_branding",
        label: T("Marquage delapresse retiré", "Delapresse branding removed", "Marca de delapresse eliminada", "إزالة العلامة التجارية لـ delapresse"),
        cells: { starter: no(), growth: no(), network: no(), agency: yes(), enterprise: yes() },
      },
      {
        id: "wl_domain",
        label: T("Console sur domaine client", "Console on customer domain", "Consola en dominio del cliente", "لوحة التحكم على نطاق العميل"),
        cells: { starter: no(), growth: no(), network: no(), agency: dash, enterprise: yes() },
      },
    ],
  },
  {
    id: "support",
    label: T("Support", "Support", "Soporte", "الدعم"),
    rows: [
      {
        id: "support_email",
        label: T("E-mail (48 h)", "Email (48 h)", "Correo electrónico (48 h)", "البريد الإلكتروني (48 ساعة)"),
        cells: { starter: yes(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "support_priority",
        label: T("Prioritaire (8 h)", "Priority (8 h)", "Prioritario (8 h)", "أولوية (8 ساعات)"),
        cells: { starter: no(), growth: yes(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "support_slack",
        label: T("Canal Slack partagé", "Shared Slack channel", "Canal compartido de Slack", "قناة Slack مشتركة"),
        cells: { starter: no(), growth: no(), network: yes(), agency: yes(), enterprise: yes() },
      },
      {
        id: "support_csm",
        label: T("CSM dédié + SLA contractuel", "Dedicated CSM + contractual SLA", "CSM dedicado + SLA contractual", "مدير حساب مخصص + SLA تعاقدي"),
        cells: { starter: no(), growth: no(), network: no(), agency: no(), enterprise: yes() },
      },
    ],
  },
];

// ---- credit packs (mirror of credit_packs seed) ------------------------------

export interface CreditPackEntry {
  sku: "pack_500" | "pack_2000" | "pack_5000" | "pack_15000";
  credits: number;
  priceCents: number;
  currency: "eur";
}

export const CREDIT_PACKS: CreditPackEntry[] = [
  { sku: "pack_500",   credits: 500,    priceCents: 1900,  currency: "eur" },
  { sku: "pack_2000",  credits: 2000,   priceCents: 5900,  currency: "eur" },
  { sku: "pack_5000",  credits: 5000,   priceCents: 12900, currency: "eur" },
  { sku: "pack_15000", credits: 15000,  priceCents: 32900, currency: "eur" },
];

/** Per-credit rate in fractional cents — useful for "save X%" copy. */
export function ratePerCreditCents(pack: CreditPackEntry): number {
  return pack.priceCents / pack.credits;
}

/** Savings vs the smallest pack as a percentage (0..1). */
export function savingsVsSmallest(pack: CreditPackEntry, smallest = CREDIT_PACKS[0]): number {
  if (pack.sku === smallest.sku) return 0;
  return 1 - ratePerCreditCents(pack) / ratePerCreditCents(smallest);
}

// ---- helpers for renderers ---------------------------------------------------

export function locText(t: BilingualText, locale: Locale): string {
  if (locale === "fr") return t.fr;
  return t[locale] || t.en;
}

const CELL_WORDS: Record<Locale, { yes: string; unlimited: string; custom: string }> = {
  fr: { yes: "Oui", unlimited: "Illimité", custom: "Sur devis" },
  en: { yes: "Yes", unlimited: "Unlimited", custom: "On request" },
  es: { yes: "Sí", unlimited: "Ilimitado", custom: "A medida" },
  ar: { yes: "نعم", unlimited: "غير محدود", custom: "حسب الطلب" },
};

export function formatCell(cell: CellValue, locale: Locale): string {
  const words = CELL_WORDS[locale];
  switch (cell.kind) {
    case "boolean":
      return cell.value ? words.yes : "—";
    case "number": {
      const n = cell.value.toLocaleString(intlLocale(locale));
      return cell.unit ? `${n} ${locText(cell.unit, locale)}` : n;
    }
    case "text":
      return locText(cell.value, locale);
    case "unlimited":
      return words.unlimited;
    case "custom":
      return words.custom;
    case "dash":
      return "—";
  }
}
